import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

import { CartItem } from 'types/event';

interface CartContextProps {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  updateCartItem: (index: number, updatedData: Partial<CartItem>) => void;
  removeFromCart: (index: number) => void;
  clearCart: () => void;
  promoterHash: string | null;
}

const CartContext = createContext<CartContextProps | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [promoterHash, setPromoterHash] = useState<string | null>(null);

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartItems(savedCart);

    const urlParams = new URLSearchParams(window.location.search);
    const hashFromUrl = urlParams.get('promoter');
    setPromoterHash(hashFromUrl);
  }, []);

  const addToCart = (item: CartItem) => {
    setCartItems((prevItems) => [...prevItems, item]);
  };

  const updateCartItem = (index: number, updatedData: Partial<CartItem>) => {
    const updatedCart = [...cartItems];
    updatedCart[index] = { ...updatedCart[index], ...updatedData };
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
  };

  const removeFromCart = (index: number) => {
    setCartItems((prevItems) => {
      const updatedCart = [...prevItems];
      updatedCart.splice(index, 1);
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      return updatedCart;
    });
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.setItem('cart', JSON.stringify([]));
  };

  return (
    <CartContext.Provider
      value={{ cartItems, addToCart, updateCartItem, removeFromCart, clearCart, promoterHash }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
